/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR298: Deal Card Preview Workbench
 * CR304: Uses centralized display helpers for title/condition
 * CR311: Full card viewing modes + Face controls + Auto-resize
 * CR307: V2-only renderer — no V1 fallback, no flicker
 * 
 * Enhanced preview container with:
 * - Display modes: Fit / Full-card / Fullscreen
 * - Face view: Front / Back / Both
 * - Simplified controls with advanced toggle
 * - ResizeObserver for auto-height in Full-card mode
 * - Stacked front+back in Both mode
 * - V2-only rendering (no renderer toggle)
 * - V2 Debug Inspector panel
 */

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Eye, RefreshCw, X, Maximize2, AlertTriangle, Ruler, Copy, Check, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { SimplePreviewControls } from "./SimplePreviewControls";
import { SnippetCoverageWidget } from "./SnippetCoverageWidget";
import { DealContextStrip } from "./DealContextStrip";
import { toUiDeal, type RawDealInput, type UIDeal } from "@/lib/deals/toUiDeal";
import type { Deal } from "@/lib/types/deals";
import type { DetailsSectionConfig } from "@/lib/layout-studio/dealCardUXTypes";
import type {
  CardFacesConfig,
  PreviewControlsState,
} from "@/lib/deals/cardFaces";
import {
  DEFAULT_PREVIEW_CONTROLS,
  DEFAULT_FACES_CONFIG,
  getEffectiveWidth,
  getScaleFactor,
  loadPreviewControls,
  savePreviewControls,
} from "@/lib/deals/cardFaces";
import type { SampleDeal } from "@/hooks/useDealCardTemplateEditor";

// CR307: V2 renderer imports (V2-only, no V1)
import { DealCardRendererV2 } from "@/components/deal-cards/DealCardRendererV2";
import { useDealCardSpecV2 } from "@/hooks/useDealCardSpecV2";
import { getDealType } from "@/lib/deals/getDealType";
import { bridgeTemplateToV2Spec, type TemplateOverrideSpec } from "@/lib/deal-cards/templateBridge";

// CR307: V2 Debug Inspector
import { V2DebugInspector } from "./V2DebugInspector";
// CR422: Config source debug chip
import { ConfigSourceChip } from "./ConfigSourceChip";
// CR429: Why Hidden diagnostics
import { WhyHiddenPanel } from "./WhyHiddenPanel";
import { computeDealCardVisibilityDiagnostics } from "@/lib/deal-cards/diagnostics/dealCardVisibilityDiagnostics";
import { SECTION_TO_BLOCK_MAP } from "@/lib/deal-cards/templateBridge";
import type { RenderPayloadMeta } from "@/hooks/useDealCardRenderPayload";
// CR313: Preview Contract Debug Panel
import { PreviewContractDebugPanel } from "./PreviewContractDebugPanel";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FLAGS } from "@/lib/featureFlags";

// Skeleton for loading state
import { Skeleton } from "@/components/ui/skeleton";

// Registry and template override types (from DealCardLivePreview)
export interface DealCardRegistryOverride {
  ribbon_text: string;
  ribbon_theme: string;
  is_enabled: boolean;
  label?: string;
}

export interface DealCardTemplateOverride {
  sections?: Record<string, boolean>;
  labels?: Record<string, string>;
  header?: Record<string, unknown>;
  extended_sections?: Record<string, boolean>;
  styling?: Record<string, unknown>;
  section_order?: string[];
  theme_preset?: string;
}

interface PreviewWorkbenchProps {
  /** The deal to preview (can be raw JSON from staging or UIDeal) */
  deal: UIDeal | Deal | Record<string, unknown> | null;
  /** CR311: Full sample with validation fields */
  sampleDeal?: SampleDeal | null;
  /** Selected deal type */
  dealType: string;
  /** Whether there are unsaved changes */
  hasUnsavedChanges?: boolean;
  /** Preview key for forcing re-renders */
  previewKey?: string | number;
  /** Registry override from draft state */
  registryOverride?: DealCardRegistryOverride | null;
  /** Template override from draft state */
  templateOverride?: DealCardTemplateOverride | null;
  /** Faces configuration */
  facesConfig?: CardFacesConfig;
  /** Details sections config (for back/drawer) */
  detailsSections?: DetailsSectionConfig[];
  /** Callback when faces config changes */
  onFacesConfigChange?: (config: CardFacesConfig) => void;
  /** Refresh callback */
  onRefresh?: () => void;
  /** Loading state */
  loading?: boolean;
  /** Is admin mode */
  isAdmin?: boolean;
  /** CR422: Environment for SSOT-aware spec loading */
  environment?: string;
  /** CR429: Render payload meta for diagnostics */
  renderMeta?: RenderPayloadMeta | null;
  className?: string;
}

// Default details sections for back/drawer preview
const WORKBENCH_DEFAULT_SECTIONS: DetailsSectionConfig[] = [
  { key: "terms", enabled: true, order: 0, flipMaxLines: 3 },
  { key: "fine_print", enabled: true, order: 1, flipMaxLines: 2 },
  { key: "source", enabled: true, order: 2 },
  { key: "evidence", enabled: true, order: 3 },
  { key: "actions", enabled: true, order: 5 },
];

export function PreviewWorkbench({
  deal,
  sampleDeal,
  dealType,
  hasUnsavedChanges = false,
  previewKey,
  registryOverride,
  templateOverride,
  facesConfig = DEFAULT_FACES_CONFIG,
  detailsSections = WORKBENCH_DEFAULT_SECTIONS,
  onFacesConfigChange,
  onRefresh,
  loading = false,
  isAdmin = false,
  environment,
  renderMeta,
  className,
}: PreviewWorkbenchProps) {
  
  // CR307: Preview controls state - initialize from localStorage, force V2
  const [controls, setControls] = useState<PreviewControlsState>(() => {
    const loaded = loadPreviewControls();
    return { ...loaded, rendererVersion: "v2" as const };
  });

  // CR313: Feature flag for contract debug panel
  const showContractDebug = useFeatureFlag(FLAGS.V2_PREVIEW_CONTRACT);
  
  // Fullscreen dialog state
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  
  // Save controls to localStorage when they change
  useEffect(() => {
    savePreviewControls(controls);
  }, [controls]);
  
  // Convert raw deal to UIDeal if needed
  const uiDeal = useMemo((): UIDeal | null => {
    if (!deal) return null;
    
    // If already a UIDeal (has _previewId), return as-is
    if ('_previewId' in deal) {
      return deal as UIDeal;
    }
    
    // Otherwise convert from raw deal JSON
    try {
      return toUiDeal(deal as RawDealInput);
    } catch (err) {
      console.error("[PreviewWorkbench] Failed to convert deal:", err);
      return null;
    }
  }, [deal]);

  // CR314 R1: Contract log — admin-only diagnostic right before V2 render
  // Logs the exact object fields the renderer will receive
  const contractViolation = useMemo(() => {
    if (!isAdmin || !uiDeal) return null;
    const d = uiDeal as any;
    const rawDeal = deal as any;
    
    // CR317: Unified contract log with source tracking
    if (typeof console !== "undefined") {
      console.log("[CR317 Contract]", {
        source: renderMeta ? "live_rpc" : "sample_view",
        id: d.id ?? d._previewId ?? null,
        dedupe_key: d.dedupe_key ?? null,
        deal_type: d.deal_category ?? d.deal_type ?? null,
        computed_value_cad: d.computed_value_cad ?? null,
        discount_amount_cad: d.discount_amount_cad ?? null,
        qualifying_spend_cad: d.qualifying_spend_cad ?? null,
        effective_percent_back: d.effective_percent_back ?? null,
        valid_from: d.start_date ?? null,
        valid_until: d.end_date ?? null,
        has_canonical: !!d.canonical_deal_json,
        contract_version: renderMeta?.contract_version ?? "unknown",
        hoisted: renderMeta?.hoisted ?? "n/a",
      });
    }

    // CR317: Hard guard — RPC contract violation (hoisted !== true)
    if (renderMeta && renderMeta.hoisted !== true) {
      return "RPC contract violated: expected hoisted=true. Deal fields may not render correctly. contract_version=" + (renderMeta.contract_version ?? "missing");
    }

    // CR314 R3: Hard guard — detect fallback deal in live mode
    const hasId = !!(d.id || (rawDeal?.id));
    const hasDk = !!(d.dedupe_key || (rawDeal?.dedupe_key));
    if (!hasId && !hasDk) {
      return "Live Preview is rendering a non-canonical deal object (missing id/dedupe_key). Check preview adapter/wiring.";
    }

    // CR317 R4: Field-level contract validation for live RPC deals
    if (renderMeta?.found) {
      const dealType = (d.deal_category ?? d.deal_type ?? rawDeal?.deal_type ?? "").toUpperCase();
      const violations: string[] = [];

      if (d.computed_value_cad == null && rawDeal?.computed_value_cad == null) {
        violations.push("computed_value_cad");
      }
      if (dealType === "DISCOUNT" && d.discount_amount_cad == null && rawDeal?.discount_amount_cad == null) {
        violations.push("discount_amount_cad (DISCOUNT deal)");
      }
      if (!d.start_date && !rawDeal?.valid_from) {
        violations.push("valid_from / start_date");
      }
      if (!d.end_date && !rawDeal?.valid_until) {
        violations.push("valid_until / end_date");
      }

      if (violations.length > 0) {
        return `Contract fields missing after hoisting: ${violations.join(", ")}. Check RPC meta.missing_fields: [${(renderMeta.missing_fields ?? []).join(", ")}]`;
      }
    }

    return null;
  }, [isAdmin, uiDeal, deal, renderMeta]);
  
  // CR307: Get deal type for V2 spec
  const dealTypeKey = useMemo(() => {
    if (!uiDeal) return null;
    return getDealType(uiDeal as unknown as Deal).category;
  }, [uiDeal]);
  
  // CR307/CR422: Load V2 spec with optional environment for SSOT
  const { spec: v2Spec, loading: v2SpecLoading, specSource } = useDealCardSpecV2({
    dealTypeKey: dealTypeKey || undefined,
    skip: !uiDeal,
    environment,
  });
  
  // CR307: Apply template overrides to V2 spec
  const bridgedV2Spec = useMemo(() => {
    if (!v2Spec) return null;
    return bridgeTemplateToV2Spec(v2Spec, templateOverride);
  }, [v2Spec, templateOverride]);

  // CR429: Compute visibility diagnostics
  const visibilityDiagnostics = useMemo(() => {
    if (!isAdmin) return null;
    const sectionOrder = templateOverride?.section_order ?? [];
    const sectionsEnabled: Record<string, boolean> = {};
    if (templateOverride?.sections) {
      for (const [k, v] of Object.entries(templateOverride.sections)) {
        if (typeof v === "boolean") sectionsEnabled[k] = v;
      }
    }
    if (templateOverride?.extended_sections) {
      for (const [k, v] of Object.entries(templateOverride.extended_sections)) {
        if (typeof v === "boolean") sectionsEnabled[k] = v;
      }
    }
    return computeDealCardVisibilityDiagnostics({
      environment: environment ?? "staging",
      dealType: dealType,
      dealId: (deal as any)?.id ?? (deal as any)?.deal_id ?? null,
      sectionOrder,
      sectionsEnabled,
      dealJson: deal as Record<string, unknown> | null,
      renderMeta: renderMeta ?? null,
      sectionToBlockMap: SECTION_TO_BLOCK_MAP,
    });
  }, [isAdmin, templateOverride, deal, renderMeta, environment, dealType]);

  // Container ref for measuring
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 600, height: 500 });
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  
  // Measure container size
  useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);
  
  // CR307: Measure content height for full-card AND fit modes (always measure)
  useEffect(() => {
    if (!contentRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(Math.ceil(entry.contentRect.height));
      }
    });
    
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);
  
  // CR307: Handle controls change, always force V2
  const handleControlsChange = useCallback((update: Partial<PreviewControlsState>) => {
    // Strip rendererVersion from update to prevent V1 switch
    const { rendererVersion, ...rest } = update as PreviewControlsState;
    setControls((prev) => ({ ...prev, ...rest, rendererVersion: "v2" as const }));
  }, []);
  
  // Handle reset to defaults
  const handleReset = useCallback(() => {
    setControls({ ...DEFAULT_PREVIEW_CONTROLS, rendererVersion: "v2" as const });
  }, []);
  
  // Toggle inherit from front
  const handleToggleInherit = useCallback((face: "back" | "drawer", inherit: boolean) => {
    if (!onFacesConfigChange) return;
    
    onFacesConfigChange({
      ...facesConfig,
      [face]: {
        ...facesConfig[face],
        inheritFromFront: inherit,
      },
    });
  }, [facesConfig, onFacesConfigChange]);
  
  // Open fullscreen when displayMode is fullscreen
  useEffect(() => {
    if (controls.displayMode === "fullscreen") {
      setFullscreenOpen(true);
    }
  }, [controls.displayMode]);
  
  // Computed values
  const effectiveWidth = getEffectiveWidth(controls.viewportWidth, controls.customWidth);
  
  // Calculate available container space
  const availableContainerWidth = containerSize.width - 32;
  const availableContainerHeight = containerSize.height - 32;
  
  const scaleFactor = controls.displayMode === "full-card" 
    ? 1 // No scaling in full-card mode
    : getScaleFactor(
        controls.scaleMode, 
        controls.customScale,
        availableContainerWidth,
        effectiveWidth,
        undefined,
        undefined
      );
  
  // Render debug overlay classes
  const overlayClasses = cn(
    controls.overlays.showSectionBounds && "debug-section-bounds",
    controls.overlays.showHiddenBlocks && "debug-hidden-blocks",
    controls.overlays.showSafePadding && "debug-safe-padding",
  );
  
  // CR307: Stable key based on deal ID + surface (no rendererVersion)
  const stableCardKey = uiDeal 
    ? `${(uiDeal as any).id || (uiDeal as any).dedupe_key || 'preview'}-${controls.faceView}` 
    : 'no-deal';
  
  // CR307: V2 is ready when spec loaded and deal exists
  const previewReady = !!bridgedV2Spec && !!uiDeal && !v2SpecLoading;
  
  // CR307: Render V2 front face only
  const renderCardFront = () => {
    // CR314 R3: Hard guard — show error panel instead of silently rendering fallback
    if (contractViolation && isAdmin) {
      return (
        <div 
          className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-destructive rounded-lg bg-destructive/10"
          style={{ width: effectiveWidth, minHeight: 200 }}
        >
          <AlertTriangle className="h-8 w-8 text-destructive" />
          <p className="text-sm font-medium text-destructive">Contract Violation</p>
          <p className="text-xs text-destructive/80 text-center max-w-md">
            {contractViolation}
          </p>
        </div>
      );
    }

    if (v2SpecLoading) {
      return <Skeleton className="rounded-lg" style={{ width: effectiveWidth, height: 440 }} />;
    }
    
    if (!bridgedV2Spec || !uiDeal) {
      return (
        <div 
          className="flex flex-col items-center justify-center gap-2 p-6 border rounded-lg bg-muted/30"
          style={{ width: effectiveWidth, minHeight: 200 }}
        >
          <AlertTriangle className="h-8 w-8 text-warning" />
          <p className="text-sm font-medium">V2 Preview unavailable</p>
          <p className="text-xs text-muted-foreground text-center">
            No V2 spec for {dealTypeKey}. Check deal_card_templates.
          </p>
        </div>
      );
    }
    
    return (
      <div 
        key={`front-${stableCardKey}`}
        className="bg-card border rounded-lg shadow-md overflow-hidden p-4" 
        style={{ width: effectiveWidth }}
      >
        <DealCardRendererV2
          deal={uiDeal as unknown as Deal}
          spec={bridgedV2Spec}
          isAdmin={isAdmin}
          surface="primary"
        />
      </div>
    );
  };
  
  // CR307: Render V2 back face only
  const renderCardBack = () => {
    if (v2SpecLoading) {
      return <Skeleton className="rounded-lg" style={{ width: effectiveWidth, height: 440 }} />;
    }
    
    if (!bridgedV2Spec || !uiDeal) {
      return (
        <div 
          className="flex flex-col items-center justify-center gap-2 p-6 border rounded-lg bg-muted/30"
          style={{ width: effectiveWidth, minHeight: 200 }}
        >
          <AlertTriangle className="h-8 w-8 text-warning" />
          <p className="text-sm font-medium">V2 Details unavailable</p>
          <p className="text-xs text-muted-foreground text-center">
            No V2 spec for {dealTypeKey}.
          </p>
        </div>
      );
    }
    
    return (
      <div 
        key={`back-${stableCardKey}`}
        className="bg-card border rounded-lg shadow-md overflow-hidden p-4" 
        style={{ width: effectiveWidth }}
      >
        <DealCardRendererV2
          deal={uiDeal as unknown as Deal}
          spec={bridgedV2Spec}
          isAdmin={isAdmin}
          surface="details"
        />
      </div>
    );
  };
  
  // Determine what to render based on faceView
  const renderContent = () => {
    if (!uiDeal) return null;
    
    if (controls.faceView === "both") {
      return (
        <div className="flex flex-col gap-4" ref={contentRef}>
          <div className="relative">
            <Badge variant="secondary" className="absolute -top-2 left-2 text-[9px] z-10">
              Front
            </Badge>
            {renderCardFront()}
          </div>
          <div className="relative">
            <Badge variant="secondary" className="absolute -top-2 left-2 text-[9px] z-10">
              Back
            </Badge>
            {renderCardBack()}
          </div>
        </div>
      );
    }
    
    if (controls.faceView === "back") {
      return <div ref={contentRef}>{renderCardBack()}</div>;
    }
    
    return <div ref={contentRef}>{renderCardFront()}</div>;
  };
  
  // Determine container height based on display mode + height mode
  const getContainerStyle = () => {
    // CR308: Height mode takes precedence
    if (controls.heightMode === "auto" || controls.displayMode === "full-card") {
      return {
        minHeight: contentHeight ? contentHeight + 32 : undefined,
        height: "auto",
      };
    }
    if (controls.heightMode === "scroll") {
      return {
        height: `${controls.customHeight || 520}px`,
        maxHeight: `${controls.customHeight || 520}px`,
      };
    }
    if (controls.heightMode === "fixed") {
      return {
        height: `${controls.customHeight || 520}px`,
      };
    }
    return {
      height: "100%",
    };
  };
  
  return (
    <>
      <Card className={cn("flex flex-col", className)}>
        <CardHeader className="p-3 pb-2 border-b space-y-2">
          {/* Top Row: Title + Context + Refresh */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* CR422: Config source debug chip */}
              {isAdmin && specSource && <ConfigSourceChip source={specSource} />}
              <Eye className="h-4 w-4 text-primary" />
              <span className="font-medium text-sm">Preview</span>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0">V2</Badge>
              {hasUnsavedChanges && (
                <Badge variant="secondary" className="text-[10px]">
                  Unsaved
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {onRefresh && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onRefresh}
                  disabled={loading}
                  className="h-7 w-7 p-0"
                >
                  <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                </Button>
              )}
            </div>
          </div>
          
          {/* CR311: Deal Context Strip */}
          <DealContextStrip sample={sampleDeal || null} />
          
          {/* CR311: Simplified Preview Controls */}
          <SimplePreviewControls
            controls={controls}
            onChange={handleControlsChange}
            onReset={handleReset}
          />
          
          {/* CR307: Recommended height readout */}
          {contentHeight != null && contentHeight > 0 && (
            <RecommendedHeightBar
              contentHeight={contentHeight}
              effectiveWidth={effectiveWidth}
              onApplyToFixed={(height) => {
                handleControlsChange({ heightMode: "fixed", customHeight: height });
              }}
            />
          )}

          {/* CR310: Snippet Coverage Widget (admin only) */}
          {isAdmin && (
            <SnippetCoverageWidget
              batchRunId={(deal as any)?.admin_batch_run_id ?? undefined}
            />
          )}
          
          {/* Inherit Toggle (for back face) */}
          {controls.selectedFace !== "front" && controls.faceView !== "both" && onFacesConfigChange && (
            <div className="flex items-center gap-2 pt-1">
              <Switch
                id="inherit-toggle"
                checked={facesConfig[controls.selectedFace].inheritFromFront}
                onCheckedChange={(checked) => 
                  handleToggleInherit(controls.selectedFace as "back" | "drawer", checked)
                }
              />
              <Label htmlFor="inherit-toggle" className="text-xs text-muted-foreground">
                Inherit from Front
              </Label>
            </div>
          )}
        </CardHeader>
        
        <CardContent 
          className={cn(
            "flex-1 p-4",
            controls.heightMode === "scroll" && "overflow-auto",
            controls.heightMode === "auto" && controls.displayMode === "full-card" && "overflow-visible",
            controls.heightMode === "auto" && controls.displayMode !== "full-card" && "overflow-auto",
            controls.heightMode === "fixed" && "overflow-hidden",
          )} 
          ref={containerRef}
          style={getContainerStyle()}
        >
          {!uiDeal ? (
            <div className="flex items-center justify-center h-full min-h-[300px] text-muted-foreground">
              <p className="text-sm">Select a sample deal to preview</p>
            </div>
          ) : (
            <div className="flex gap-4 justify-start">
              {/* ViewportFrame: owns width constraint */}
              <div
                className={cn(
                  "rounded-xl border bg-card relative",
                  controls.displayMode === "full-card" && "border-none bg-transparent"
                )}
                style={{
                  width: effectiveWidth,
                  flex: "none",
                  alignSelf: "flex-start",
                  overflow: controls.displayMode === "full-card" ? "visible" : "hidden",
                }}
              >
                {/* Fold Line Overlay */}
                {controls.overlays.showFoldLine && (
                  <div 
                    className="absolute left-0 right-0 border-t-2 border-dashed border-warning pointer-events-none z-50"
                    style={{ top: "200px" }}
                  >
                    <span className="absolute -top-5 left-2 text-[9px] text-warning font-medium bg-background px-1 rounded">
                      Fold line (200px)
                    </span>
                  </div>
                )}
                
                {/* Content Container */}
                <div 
                  className={cn(
                    "w-full",
                    controls.displayMode === "fit" && "h-full overflow-auto",
                    controls.displayMode === "full-card" && "overflow-visible",
                  )}
                >
                  {/* ScaledContent */}
                  <div
                    className={cn(
                      "transition-transform duration-200",
                      overlayClasses,
                    )}
                    style={{
                      transform: scaleFactor !== 1 ? `scale(${scaleFactor})` : undefined,
                      transformOrigin: "top left",
                      width: "fit-content",
                    }}
                  >
                    <div key={previewKey}>
                      {renderContent()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* CR429: Why Hidden diagnostics panel */}
          {isAdmin && visibilityDiagnostics && (
            <WhyHiddenPanel diagnostics={visibilityDiagnostics} className="mt-3" />
          )}

          {/* CR313: Preview Contract Debug Panel (behind feature flag) */}
          {isAdmin && showContractDebug && uiDeal && (
            <PreviewContractDebugPanel
              deal={uiDeal as unknown as Record<string, unknown>}
              className="mt-3"
            />
          )}

          {/* CR307: V2 Debug Inspector (always V2, no condition on rendererVersion) */}
          {bridgedV2Spec && uiDeal && (
            <V2DebugInspector
              spec={bridgedV2Spec}
              templateOverride={templateOverride as Record<string, unknown> | undefined}
              dealTypeKey={dealTypeKey || undefined}
              defaultOpen={false}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>
      
      {/* CR311: Fullscreen Dialog */}
      <Dialog open={fullscreenOpen} onOpenChange={(open) => {
        setFullscreenOpen(open);
        if (!open) {
          // Reset to fit mode when closing fullscreen
          handleControlsChange({ displayMode: "fit" });
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Maximize2 className="h-4 w-4" />
                Full Preview
                <Badge variant="outline" className="text-[9px] px-1.5 py-0">V2</Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFullscreenOpen(false)}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {uiDeal && (
              <div className="flex flex-col items-center gap-6">
                {(controls.faceView === "front" || controls.faceView === "both") && (
                  <div className="relative">
                    {controls.faceView === "both" && (
                      <Badge variant="secondary" className="absolute -top-3 left-2 text-xs z-10">
                        Front
                      </Badge>
                    )}
                    <div style={{ width: effectiveWidth }}>
                      {renderCardFront()}
                    </div>
                  </div>
                )}
                
                {(controls.faceView === "back" || controls.faceView === "both") && (
                  <div className="relative">
                    {controls.faceView === "both" && (
                      <Badge variant="secondary" className="absolute -top-3 left-2 text-xs z-10">
                        Back
                      </Badge>
                    )}
                    {renderCardBack()}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ================================================================
// CR307: Recommended Height Bar
// ================================================================

function RecommendedHeightBar({ contentHeight, effectiveWidth, onApplyToFixed }: { contentHeight: number; effectiveWidth: number; onApplyToFixed?: (height: number) => void }) {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const recommended = contentHeight + 24; // add padding

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(String(recommended)).then(() => {
      setCopied(true);
      toast.success("Copied for Layout Studio!");
      setTimeout(() => setCopied(false), 1500);
    });
  }, [recommended]);

  const handleApply = useCallback(() => {
    onApplyToFixed?.(recommended);
  }, [recommended, onApplyToFixed]);

  const handleOpenLayoutStudio = useCallback(() => {
    navigate(`/admin/layout-studio?prefillHeight=${recommended}`);
  }, [recommended, navigate]);

  return (
    <div className="flex items-center gap-2 px-2 py-1 bg-muted/40 rounded border border-border/50 text-[11px]">
      <Ruler className="h-3 w-3 text-muted-foreground flex-shrink-0" />
      <span className="text-muted-foreground">
        Content: <span className="font-mono font-medium text-foreground">{effectiveWidth}×{contentHeight}px</span>
      </span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">
        Recommended: <span className="font-mono font-medium text-foreground">{recommended}px</span>
      </span>
      {onApplyToFixed && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-5 px-1.5 text-[10px] gap-0.5"
              onClick={handleApply}
            >
              Apply to Fixed
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            Set height mode to Fixed at {recommended}px
          </TooltipContent>
        </Tooltip>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0"
            onClick={handleCopy}
          >
            {copied ? <Check className="h-3 w-3 text-primary" /> : <Copy className="h-3 w-3 text-muted-foreground" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {copied ? "Copied for Layout Studio!" : "Copy height for Layout Studio"}
        </TooltipContent>
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-5 px-1.5 text-[10px] gap-1 ml-auto"
            onClick={handleOpenLayoutStudio}
          >
            <ExternalLink className="h-3 w-3" />
            Open Layout Studio
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          Open Layout Studio with {recommended}px prefilled
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
